import Vue from 'vue'
import { arrayFind, arrayFindIndex, debounce } from 'util/ts-utils'

import withRender from './listbox.html'
import './listbox.scss'

export default withRender(Vue.extend({
  data() {
    return {
      activeIndex: -1,
      ident: Math.random().toString(16).slice(5),
      isExpanded: false,
      searchVal: '',
      value: '',  // for single select
      values: [] // for multi-select
    }
  },
  props: {
    // when true, selecting a value that is already selected, deselects it.  When false, the value
    // remains selected.  This is to handle the disctinction between sort and a single value filter
    // such as "verified purchaser".
    // For sort, this is false.  For filters it is true.
    deselectable: Boolean,
    disabled: Boolean,  // truly disabled. cannot interact with
    isRating: Boolean,
    label: String,
    model: [Array, String, Number, Boolean],
    multiSelect: Boolean,
    options: { type: Array, required: true },
    subLabel: String
  },
  model: {
    prop: 'model',
    event: 'change'
  },
  computed: {
    activeDesc() {
      return this.activeIndex > -1 ? this.optionsData[this.activeIndex].id  : ''
    },
    btnValue() {
      if (this.subLabel) {
        return this.subLabel
      }
      // assumption is subLabel is always populated for multiSelect listbox.  This only checks
      // for this.value
      const selectedOption = arrayFind(this.options, (opt) => {
        return opt.value === this.value
      })
      // TODO: need a 'select one' option?  Don't yet, but maybe need for fallback
      return selectedOption ?
        (selectedOption.labelKey ? this.$msg(selectedOption.labelKey) : selectedOption.label) : ''
    },
    hasSelection() {
      return !!this.value || this.values.length > 0
    },
    optionsData() {
      return this.options.map((opt, i) => {
        const isSelected = this.value === opt.value || this.values.indexOf(opt.value) > -1
        return {
          id: `tt-option-${this.ident}-${i}`,
          label: opt.labelKey ? this.$msg(opt.labelKey) : opt.label,
          isSelected,
          iconName: this.multiSelect && isSelected ? 'checkbox--on' :
            (this.multiSelect && !isSelected ? 'checkbox--off' :
            (!this.multiSelect && isSelected ? 'check' : '')),
          iconTitle: isSelected ? this.$msg('listbox.check') : ''
        }
      })
    }
  },
  methods: {
    handleKeydown(e: KeyboardEvent) {
      if (e.keyCode === 9) { // tab - still want to allow tab to continue, so not stop or prevent
        this.setExpanded(false)
      } else if (e.keyCode === 27) { // esc
        e.stopPropagation()
        this.setExpanded(false)
      } else if (e.keyCode === 38) { // arrow up
        e.preventDefault()
        this.moveUp()
      } else if (e.keyCode === 40) { // arrow down
        e.preventDefault()
        this.moveDown()
      } else if (e.keyCode === 36) { // home
        e.preventDefault()
        this.activeIndex = 0
      } else if (e.keyCode === 35) { // end
        e.preventDefault()
        this.activeIndex = this.options.length - 1
      } else if (e.keyCode === 32 || e.keyCode === 13) { // space bar or return
        e.preventDefault()
        if (this.deselectable || e.keyCode === 13) {
          this.selectOption(this.activeIndex)
        }
      } else {
        this.searchVal = this.searchVal + String.fromCharCode(e.keyCode).toLowerCase()
        this.findByText()
      }
    },
    findByText: debounce(function cb() {
      const lastItemIndex = this.options.length - 1
      let currentIndex = this.activeIndex === lastItemIndex ? 0 : this.activeIndex + 1
      for (let i = 0; i < lastItemIndex; i++) {
        if (this.options[currentIndex].label.toLowerCase().indexOf(this.searchVal) === 0) {
          this.activeIndex = currentIndex
          break
        }
        currentIndex = currentIndex === lastItemIndex ? 0 : currentIndex + 1
      }
      this.searchVal = ''
    }, 300),
    moveDown() {
      if (this.activeIndex < this.options.length - 1) {
        this.activeIndex = this.activeIndex + 1
      }
    },
    moveUp() {
      if (this.activeIndex > 0) {
        this.activeIndex = this.activeIndex - 1
      }
    },
    outsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.setExpanded(false)
      }
    },
    selectOption(index) {
      const isSelected = this.optionsData[index].isSelected
      const val = this.options[index].value
      if (!this.deselectable) {
        this.value = val
        this.$nextTick(() => this.setExpanded(false))
        return
      } else if (!this.multiSelect) { // single value filter
        this.value = isSelected ? '' : val
      } else if (isSelected) { // multi-value filter where this value is being deselected
        this.values.splice(this.values.indexOf(val), 1)
      } else { // multi-value filter where this value is being selected
        this.values.push(val)
      }
      this.activeIndex = index
    },
    setValues() {
      if (this.model !== undefined) {
        if (this.multiSelect) {
          this.values = this.model
        } else {
          this.value = this.model
        }
      }
    },
    setExpanded(expand) {
      if (this.disabled && expand === true) {
        return
      }
      this.isExpanded = expand
      if (!this.isExpanded) {
        document.body.removeEventListener('click', this.outsideClick, false)
        this.$refs.trigger.focus()
        this.activeIndex = -1
        return
      }
      this.$nextTick(() => {
        // focus goes on the list (ul). The appearance of focus on individual items (li) is
        // achieved with styling and with 'aria-activedescendant' on the list (ul)
        this.$refs.list.focus()
        let firstValue = this.value
        if (this.values.length > 0) {
          firstValue = this.values[0]
        }
        this.activeIndex = !firstValue ? 0 :
        this.activeIndex = arrayFindIndex(this.options, opt => opt.value === firstValue)
        document.body.addEventListener('click', this.outsideClick, false)
      })
    }
  },
  watch: {
    model() {
      this.setValues()
    },
    value() {
      this.$emit('change', this.value)
    },
    values() {
      this.$emit('change', this.values)
    }
  },
  created() {
    this.setValues()
  },
  beforeDestroy() {
    // just to be sure we don't leave the event around
    document.body.removeEventListener('click', this.outsideClick, false)
  }
}))



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/objects/listbox/listbox.ts